import { Title, Subtitle, Body, Bold, Link, List, ListItem } from "@panwds/react-ui";
import { WrenchIcon, LockIcon, LinkIcon, CircleCheckIcon, BoltIcon } from "@panwds/icons";
import { ImgIcon } from "@panwds/react-ui";
import AiActIcon from '../../../images/ai_act.svg';
import CngfwRulestackIcon from '../../../images/cngfw_rulestack.svg';
import UserExperienceIcon from '../../../images/user_experience.svg';
import { useTranslate } from "../../../customHooks";
import { SUBSCRIPTION_I18N_KEYS } from '../constants';
import { FormattedMessage } from "react-intl";
import { ListingType } from '../../../interfaces/interfaces';
import { SUBSCRIPTION_ACCOUNT_STATUS } from '../../../utils/constants';

/**
 * Help content components for subscription registration
 * Displays different content based on product type and billing code status
 */

interface HelpContentProps {
    hasCode: string;
    listing: ListingType;
    accountStatus?: string;
}

/**
 * Help content for Cloud NGFW with billing code (PAYG)
 */
const CloudNgfwPaygContent = () => {
    const translate = useTranslate();

    return (
        <>
            <Subtitle size="lg" addClassName="tw-mb-2">{translate(SUBSCRIPTION_I18N_KEYS.CNGFW_PAYG_TITLE)}</Subtitle>

            <Title level={2} size="lg" addClassName="tw-mb-4 tw-font-bold">{translate(SUBSCRIPTION_I18N_KEYS.CNGFW_PAYG_SUBTITLE)}</Title>

            <Body size="md" addClassName="tw-mb-1" style={{ fontSize: '16px' }}>
                {translate(SUBSCRIPTION_I18N_KEYS.CNGFW_PAYG_DESCRIPTION_1)}
            </Body>

            <Body size="md" addClassName="tw-mb-4" style={{ fontSize: '16px' }}>
                {translate(SUBSCRIPTION_I18N_KEYS.CNGFW_PAYG_DESCRIPTION_2)}
            </Body>

            <List addClassName="tw-my-4 !tw-border-0 !tw-shadow-none !tw-pl-0" style={{ border: 'none', boxShadow: 'none', paddingLeft: 0, overflow: 'visible' }}>
                <ListItem iconLeft={<LinkIcon size="xl" />} addClassName="tw-mb-5" listItemStyle={{ background: 'none', paddingLeft: 0 }}>
                    <Subtitle size="lg" style={{ fontSize: '20px' }}><Bold>{translate(SUBSCRIPTION_I18N_KEYS.CNGFW_PAYG_LINK_BILLING_TITLE)}</Bold></Subtitle>
                    <Body size="md" style={{ fontSize: '16px' }}>{translate(SUBSCRIPTION_I18N_KEYS.CNGFW_PAYG_LINK_BILLING_DESC)}</Body>
                </ListItem>

                <ListItem iconLeft={<CircleCheckIcon size="xl" />} addClassName="tw-mb-5" listItemStyle={{ background: 'none', paddingLeft: 0 }}>
                    <Subtitle size="lg" style={{ fontSize: '20px' }}><Bold>{translate(SUBSCRIPTION_I18N_KEYS.CNGFW_PAYG_PROTECTION_TITLE)}</Bold></Subtitle>
                    <Body size="md" style={{ fontSize: '16px' }}>{translate(SUBSCRIPTION_I18N_KEYS.CNGFW_PAYG_PROTECTION_DESC)}</Body>
                </ListItem>
            </List>

            <Body size="md" appearance="secondary" style={{ fontSize: '16px' }}>
                <FormattedMessage
                    id={SUBSCRIPTION_I18N_KEYS.CNGFW_PAYG_SUPPORT_TEXT}
                    values={{
                        supportEmail: <Bold>{translate(SUBSCRIPTION_I18N_KEYS.HELP_SUPPORT_EMAIL)}</Bold>,
                        supportLink: <Link external href="https://support.paloaltonetworks.com/Support/Index">{translate(SUBSCRIPTION_I18N_KEYS.HELP_SUPPORT_LINK)}</Link>
                    }}
                />
            </Body>
        </>
    );
};

/**
 * Help content for Cloud NGFW trial (no billing code)
 */
const CloudNgfwTrialContent = () => {
    const translate = useTranslate();

    return (
        <>
            <Subtitle size="lg" addClassName="tw-mb-2">{translate(SUBSCRIPTION_I18N_KEYS.CNGFW_TRIAL_TITLE)}</Subtitle>

            <Title level={3} size="lg" addClassName="tw-mb-4 tw-font-bold">{translate(SUBSCRIPTION_I18N_KEYS.CNGFW_TRIAL_SUBTITLE)}</Title>

            <Body size="md" addClassName="tw-mb-4" style={{ fontSize: '16px' }}>
                {translate(SUBSCRIPTION_I18N_KEYS.CNGFW_TRIAL_DESCRIPTION)}
            </Body>

            <List addClassName="tw-my-4 !tw-border-0 !tw-shadow-none !tw-pl-0" style={{ border: 'none', boxShadow: 'none', paddingLeft: 0, overflow: 'visible' }}>
                <ListItem iconLeft={<BoltIcon size="xl" />} addClassName="tw-mb-5" listItemStyle={{ background: 'none', paddingLeft: 0 }}>
                    <Subtitle size="lg" style={{ fontSize: '20px' }}><Bold>{translate(SUBSCRIPTION_I18N_KEYS.CNGFW_TRIAL_SIMPLE_SETUP_TITLE)}</Bold></Subtitle>
                    <Body size="md" style={{ fontSize: '16px' }}>{translate(SUBSCRIPTION_I18N_KEYS.CNGFW_TRIAL_SIMPLE_SETUP_DESC)}</Body>
                </ListItem>

                <ListItem iconLeft={<WrenchIcon size="xl" />} addClassName="tw-mb-5" listItemStyle={{ background: 'none', paddingLeft: 0 }}>
                    <Subtitle size="lg" style={{ fontSize: '20px' }}><Bold>{translate(SUBSCRIPTION_I18N_KEYS.CNGFW_TRIAL_CUSTOMIZE_TITLE)}</Bold></Subtitle>
                    <Body size="md" style={{ fontSize: '16px' }}>{translate(SUBSCRIPTION_I18N_KEYS.CNGFW_TRIAL_CUSTOMIZE_DESC)}</Body>
                </ListItem>

                <ListItem iconLeft={<LockIcon size="xl" />} addClassName="tw-mb-5" listItemStyle={{ background: 'none', paddingLeft: 0 }}>
                    <Subtitle size="lg" style={{ fontSize: '20px' }}><Bold>{translate(SUBSCRIPTION_I18N_KEYS.CNGFW_TRIAL_SECURE_TITLE)}</Bold></Subtitle>
                    <Body size="md" style={{ fontSize: '16px' }}>{translate(SUBSCRIPTION_I18N_KEYS.CNGFW_TRIAL_SECURE_DESC)}</Body>
                </ListItem>
            </List>

            <Body size="md" addClassName="tw-mt-4" style={{ fontSize: '16px' }}><Bold>{translate(SUBSCRIPTION_I18N_KEYS.CNGFW_TRIAL_FEATURES_INTRO)}</Bold></Body>
            <List addClassName="tw-my-4 !tw-border-0 !tw-shadow-none" style={{ listStyleType: 'disc', paddingLeft: '1.25rem', border: 'none', boxShadow: 'none', overflow: 'visible' }}>
                <ListItem listItemStyle={{ display: 'list-item', background: 'none' }} addClassName="tw-mb-3 tw-leading-relaxed"><Body size="md" as="span" style={{ fontSize: '16px' }}>{translate(SUBSCRIPTION_I18N_KEYS.CNGFW_TRIAL_FEATURE_1)}</Body></ListItem>
                <ListItem listItemStyle={{ display: 'list-item', background: 'none' }} addClassName="tw-mb-3 tw-leading-relaxed"><Body size="md" as="span" style={{ fontSize: '16px' }}>{translate(SUBSCRIPTION_I18N_KEYS.CNGFW_TRIAL_FEATURE_2)}</Body></ListItem>
                <ListItem listItemStyle={{ display: 'list-item', background: 'none' }} addClassName="tw-mb-3 tw-leading-relaxed"><Body size="md" as="span" style={{ fontSize: '16px' }}>{translate(SUBSCRIPTION_I18N_KEYS.CNGFW_TRIAL_FEATURE_3)}</Body></ListItem>
            </List>

            <Body size="md" appearance="secondary" style={{ fontSize: '16px' }}>
                <FormattedMessage
                    id={SUBSCRIPTION_I18N_KEYS.CNGFW_TRIAL_SUPPORT_TEXT}
                    values={{
                        supportEmail: <Bold>{translate(SUBSCRIPTION_I18N_KEYS.HELP_SUPPORT_EMAIL)}</Bold>,
                        supportLink: <Link external href="https://support.paloaltonetworks.com/Support/Index">{translate(SUBSCRIPTION_I18N_KEYS.HELP_SUPPORT_LINK)}</Link>
                    }}
                />
            </Body>
        </>
    );
};

/**
 * Help content for AIRFM (unified for both trial and PAYG)
 */
const AirfmContent = () => {
    const translate = useTranslate();

    return (
        <>
            <Subtitle size="lg" addClassName="tw-mb-2">{translate(SUBSCRIPTION_I18N_KEYS.AIRFM_WELCOME_TITLE)}</Subtitle>

            <Title level={2} size="lg" addClassName="tw-mb-4 tw-font-bold">{translate(SUBSCRIPTION_I18N_KEYS.AIRFM_GET_STARTED_TITLE)}</Title>

            <ul className="tw-mb-4 tw-pl-5" style={{ listStyleType: 'disc' }}>
                <li className="tw-mb-3 tw-leading-relaxed">
                    <Body size="md" style={{ fontSize: '16px' }}>
                        <Bold>{translate(SUBSCRIPTION_I18N_KEYS.AIRFM_NEW_USER_TITLE)}</Bold>{' '}{translate(SUBSCRIPTION_I18N_KEYS.AIRFM_NEW_USER_DESC)}
                    </Body>
                </li>
                <li className="tw-mb-3 tw-leading-relaxed">
                    <Body size="md" style={{ fontSize: '16px' }}>
                        <Bold>{translate(SUBSCRIPTION_I18N_KEYS.AIRFM_EXISTING_USER_TITLE)}</Bold>{' '}{translate(SUBSCRIPTION_I18N_KEYS.AIRFM_EXISTING_USER_DESC)}
                    </Body>
                </li>
            </ul>

            <Body size="md" addClassName="tw-mt-4" style={{ fontSize: '16px' }}><Bold>{translate(SUBSCRIPTION_I18N_KEYS.AIRFM_WHY_CHOOSE_TITLE)}</Bold></Body>

            <List addClassName="tw-my-4 !tw-border-0 !tw-shadow-none !tw-pl-0" style={{ border: 'none', boxShadow: 'none', paddingLeft: 0, overflow: 'visible' }}>
                <ListItem iconLeft={<div style={{ width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><ImgIcon src={AiActIcon} alt="AI Act" size="xl" /></div>} addClassName="tw-mb-5" listItemStyle={{ background: 'none', paddingLeft: 0, overflow: 'visible', whiteSpace: 'normal' }}>
                    <Subtitle size="lg" style={{ fontSize: '20px' }}><Bold>{translate(SUBSCRIPTION_I18N_KEYS.AIRFM_THREAT_PROTECTION_TITLE)}</Bold></Subtitle>
                    <Body size="md" style={{ fontSize: '16px', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'normal' }}>{translate(SUBSCRIPTION_I18N_KEYS.AIRFM_THREAT_PROTECTION_DESC)}</Body>
                </ListItem>

                <ListItem iconLeft={<div style={{ width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><ImgIcon src={CngfwRulestackIcon} alt="CNGFW Rulestack" size="xl" /></div>} addClassName="tw-mb-5" listItemStyle={{ background: 'none', paddingLeft: 0, overflow: 'visible', whiteSpace: 'normal' }}>
                    <Subtitle size="lg" style={{ fontSize: '20px' }}><Bold>{translate(SUBSCRIPTION_I18N_KEYS.AIRFM_SCALING_TITLE)}</Bold></Subtitle>
                    <Body size="md" style={{ fontSize: '16px', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'normal' }}>{translate(SUBSCRIPTION_I18N_KEYS.AIRFM_SCALING_DESC)}</Body>
                </ListItem>

                <ListItem iconLeft={<div style={{ width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><ImgIcon src={UserExperienceIcon} alt="User Experience" size="xl" /></div>} addClassName="tw-mb-5" listItemStyle={{ background: 'none', paddingLeft: 0, overflow: 'visible', whiteSpace: 'normal' }}>
                    <Subtitle size="lg" style={{ fontSize: '20px' }}><Bold>{translate(SUBSCRIPTION_I18N_KEYS.AIRFM_COMPLIANCE_TITLE)}</Bold></Subtitle>
                    <Body size="md" style={{ fontSize: '16px', whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'normal' }}>{translate(SUBSCRIPTION_I18N_KEYS.AIRFM_COMPLIANCE_DESC)}</Body>
                </ListItem>
            </List>

            <Body size="md" appearance="secondary" style={{ fontSize: '16px' }}>
                <FormattedMessage
                    id={SUBSCRIPTION_I18N_KEYS.AIRFM_SUPPORT_TEXT}
                    values={{
                        supportEmail: <Link external href="mailto:support@paloaltonetworks.com">{translate(SUBSCRIPTION_I18N_KEYS.HELP_SUPPORT_EMAIL)}</Link>,
                        supportLink: <Link external href="https://support.paloaltonetworks.com/Support/Index">support portal</Link>
                    }}
                />
            </Body>
        </>
    );
};

/**
 * Main help content component that renders appropriate content based on product and billing status
 */
export const HelpContent = ({ hasCode, listing, accountStatus }: HelpContentProps) => {
    // Always show PAYG content for linked or in_metering_group accounts
    const isLinkedOrMetering = accountStatus === SUBSCRIPTION_ACCOUNT_STATUS.LINKED ||
        accountStatus === SUBSCRIPTION_ACCOUNT_STATUS.IN_METERING_GROUP;
    const hasPayg = isLinkedOrMetering || hasCode === "Yes";

    // AIRFM (unified panel for both trial and PAYG)
    if (listing === 'AIRFM') {
        return <AirfmContent />;
    }

    // Cloud NGFW with billing code
    if (listing === 'CNGFW' && hasPayg) {
        return <CloudNgfwPaygContent />;
    }

    // Cloud NGFW trial (no billing code) - default
    return <CloudNgfwTrialContent />;
};
